import * as React from 'react';
import { compose, withProps, withState, withHandlers } from 'recompose';
import { translate } from 'react-i18next';
import { TranslateProps } from 'util/i18n';
import { skin, OemProps } from 'ui/styles/skin';
import { UtcTime } from 'types';
import * as moment from 'moment';
import { Moment } from 'moment';
import { DownTriangle, X } from 'ui/shapes';

import './DatePicker.css';

export type OwnProps = {
    value: Date; // beginning of the selected day in the server's timezone
    today: UtcTime; // start of today in the server's timezone
    onAccept: (d: Date) => void;
    onClose: () => void;
    tzOffsetCalculator: (t: number) => number; // calculates minutes offset from UTC
};

type RecomposeProps = {
    m: Moment; // server date
    selected: Moment; // server date
    today: Moment; // server date
    setMoment: (m: Moment) => void;
    onChange: (m: Moment) => () => void;
    handleSelectDay: (m: Moment) => () => void;
    styles: { [name: string]: React.CSSProperties };
};

const YearPicker = ({ m, onChange, styles }: RecomposeProps) =>
    <div className="year-picker">
        <span className="prev-year" onClick={ onChange(m.clone().subtract(1, 'year')) }>
            <DownTriangle width=".6em" height=".6em" style={ styles.foreground }/>
        </span>
        <span className="cur-year">
            { m.year() }
        </span>
        <span className="next-year" onClick={ onChange(m.clone().add(1, 'year')) }>
            <DownTriangle width=".6em" height=".6em" style={ styles.foreground }/>
        </span>
    </div>
;


const MonthPicker = ({ m, onChange, t, styles }: RecomposeProps & TranslateProps) =>
    <div className="month-picker">
        <span className="prev-month" onClick={ onChange(m.clone().subtract(1, 'month')) }>
            <DownTriangle width=".6em" height=".6em" style={ styles.foreground }/>
        </span>
        <span className="cur-month">
            { m.format('MMMM') }
        </span>
        <span className="next-month" onClick={ onChange(m.clone().add(1, 'month')) }>
            <DownTriangle width=".6em" height=".6em" style={ styles.foreground }/>
        </span>
    </div>
;


const DayNames = ({ t, styles }: TranslateProps & OemProps) => {
    const names = [
        t('6.1238.72')[0], // Sun
        t('6.1238.73')[0],
        t('6.1238.74')[0],
        t('6.1238.75')[0],
        t('6.1238.76')[0],
        t('6.1238.77')[0],
        t('6.1238.78')[0], // Sat
    ];
    return (
        <div className="day-names" style={ (styles.DatePicker as any).DayNames }>
            { names.map((name, i) =>
                <div key={ i } className="day-name">
                    { name }
                </div>
            ) }
        </div>
    );
};

// Given a date, what is the first date that would appear on the calendar for that month?
// e.g. for Jun 26, 2017, the first display date is Sunday, May 28
export const getFirstDisplayDate = (m: Moment) => {
    const firstOfTheMonth = m.clone().date(1);
    const dayOfWeek = firstOfTheMonth.isoWeekday();
    return firstOfTheMonth.subtract(dayOfWeek ? dayOfWeek : 7, 'days');
};


const getDayClassName = (day: Moment, today: Moment, m: Moment) =>
    [
        'day-picker-day',
        day.isSame(m, 'month') ? 'active-month' : '',
        day.isSame(m, 'day') ? 'active-day' : '',
        day.isAfter(today) ? 'future-day' : '',
    ]
    .join(' ')
;

const noop = () => {};

const DayPicker = ({ m, today, selected, onChange, t, styles }: RecomposeProps & TranslateProps & OemProps) => {
    const firstDisplayDate = getFirstDisplayDate(m);

    // Moment gives us a mutable object that doesn't play well with functional programming...
    // so do an old-school for-loop to set up our array of days.
    const days: Moment[] = [];
    for (let i = 0; i < 42; i++) { // 42 for 6 weeks x 7 days
        const day = firstDisplayDate.clone();
        day.add(i, 'days');
        days.push(day);
    }

    return (
        <div className="day-picker">
            <DayNames t={ t } styles={ styles }/>
            { days.map((day, i) =>
                <div
                    key={ `${i}_${day.format('l')}` }
                    className={ getDayClassName(day, today, m) }
                    style={ day.isSame(selected, 'day') ? (styles.DatePicker as any).ActiveDay : {} }
                    onClick={ day.isAfter(today) ? noop : onChange(day) }
                >
                    { day.date() }
                </div>
            ) }
        </div>
    );
};


const RawDatePicker = ({ onAccept, value, styles, t, ...props }: OwnProps & RecomposeProps & TranslateProps) =>
    <div style={ styles.datePicker } className="date-picker">
        <div style={ styles.relative }>
            <span>
                { t('260') }
            </span>
            <span onClick={ props.onClose } style={ styles.cancelButton }>
                <X width="10px" height="10px" style={ styles.foreground }/>
            </span>
        </div>
        <div style={ styles.header }>
            <div className="month-and-year">
                <MonthPicker { ...props } t={ t } styles={ styles }/>
                <YearPicker { ...props } styles={ styles }/>
            </div>
            <div>
                <DayPicker { ...props } onChange={ props.handleSelectDay } t={ t } styles={ styles }/>
            </div>
        </div>
    </div>
;


export const DatePicker = compose<OwnProps & RecomposeProps & OemProps & TranslateProps, OwnProps>(
    translate(),
    skin,
    withProps(({ value, today, tzOffsetCalculator }) => ({
        today: moment.utc(today).utcOffset(tzOffsetCalculator(today)),
        selected: moment.utc(value).utcOffset(tzOffsetCalculator(value.getTime())),
    })),
    withState('m', 'setMoment', ({ selected }: RecomposeProps) =>
        selected
    ),
    withHandlers({
        onChange: ({ setMoment }: RecomposeProps) => (m: Moment) => () => setMoment(m),
        handleSelectDay: ({ setMoment, onAccept }: RecomposeProps & OwnProps) => (m: Moment) => () => {
            setMoment(m);
            onAccept((m as Moment).toDate());
        },
    }),
    withProps(({ styles }: OemProps) => ({
        styles: {
            ...styles,
            foreground: {
                fill: styles.Window.color,
            },
            relative: {
                position: 'relative',
            },
            datePicker: {
                ...styles.Bordered,
                ...styles.Workspace,
            },
            header: {
                ...styles.Bordered,
                ...styles.Window,
                margin: '0 .5em',
            },
            cancelButton: {
                float: 'right',
                cursor: 'pointer',
            },
        },
    })),
)(RawDatePicker);



// WEBPACK FOOTER //
// ./src/ui/pages/monitoring/sidePanel/DatePicker.tsx